import { Dropdown, Menu, Progress } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { AppButton } from "components/button/AppButton";
import { routePath } from "config/routeMgt/routePath";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { SendInvite } from "./SendInvite";
import { AddToFolderForm } from "features/folders/components/AddToFolderForm";
import { ReportTalent } from "./ReportTalent";
import { useFetchTalent } from "../hooks/useFetchTalent";
import { usePagination } from "hooks/usePagination";
import { ITalent } from "../types";
import userDp from "assets/images/placeholderImage.jpeg";
import { useGlobalContext } from "stateManagers/GlobalContext";
import { SendMessageModal } from "features/message/components/SendMessageModal";
import { TableRowShimmer } from "components/layout/components/loaders/Shimmer";
import { useCheckPermission } from "components/layout/sidebar/hooks/useHasPermission";
// import { MOCK_TALENTS } from "./mockTalents";

export const TalentsTable = forwardRef((props: any, ref: any) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [inviteModal, setInviteModal] = useState(false);
  const [talentId, setTalentId] = useState<string>();
  const [addToFolder, setAddToFolder] = useState(false);
  const { filterTalent, setFilterTalent } = useGlobalContext();
  const [reportModaL, setReportModaL] = useState(false);
  const { pagination, onChange } = usePagination();
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string>();
  const { searchTerm } = props;

  useEffect(() => {
    if (
      !filterTalent.sector &&
      !filterTalent.experience &&
      !filterTalent.gender &&
      !filterTalent.country &&
      !filterTalent.skills &&
      !filterTalent.jobTitle &&
      !filterTalent.visibility_score &&
      !filterTalent.relocate
    ) {
    }
  }, [filterTalent]);

  useImperativeHandle(ref, () => ({
    getFilteredData: () => data?.data || [],
  }));

  const { data, isFetching, refetch } = useFetchTalent({
    pagination: {
      pageSize: pagination.pageSize,
      current: pagination.current,
    },
    experience: filterTalent.experience,
    country: filterTalent.country,
    skills: filterTalent.skills,
    gender: filterTalent.gender,
    visibilityScore: filterTalent.visibility_score ?? null,
    relocate: filterTalent.relocate ?? null,
    sector: filterTalent.sector,
    search: searchTerm,
  });

  // const data = { data: MOCK_TALENTS, total: MOCK_TALENTS.length };
  // const isFetching = false;
  // const refetch = () => {};

  const getCurrentJob = (workHistories: any[]) => {
    return workHistories.find((job) => job.is_current === 1);
  };

  const handleClear = () => {
    setSelectedRowKeys([]);
    setTalentId(undefined);
    setAddToFolder(false);
  };

  const sendInvite = (id: string) => {
    setInviteModal(true);
    setTalentId(id);
  };

  const handleAddToFolder = (id: string) => {
    setAddToFolder(true);
    setTalentId(id);
  };

  useEffect(() => {
    return () => {
      onChange({ current: 1, pageSize: pagination.pageSize });
    };
  }, []);

  const hasSelected = selectedRowKeys.length > 0;

  const filteredData = data?.data;

  const formatExperienceLevel = (level: string): string => {
    return level
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleSendMessage = (id: string) => {
    setSelectedTalentId(id);
    setMessageModalOpen(true);
  };

  const canCreateInvite = useCheckPermission("invite.create");
  const canViewMessages = useCheckPermission("message.view");
  const canCreateFolder = useCheckPermission("folder.create");

  const formatFullName = (
    firstName: string,
    lastName: string,
    middleName?: string | null,
  ): string => {
    const nameParts = [firstName, middleName, lastName].filter(
      (name) => name && name !== "null" && name.trim() !== "",
    );
    return nameParts.join(" ");
  };

  const columns: ColumnsType<ITalent> = [
    {
      title: "Name",
      dataIndex: "name",
      render: (_, val) => (
        <div className="flex items-center">
          <img
            src={val?.dp ? val?.dp : userDp}
            alt="avatar"
            className="rounded-full h-10 w-10 object-cover object-top"
          />

          <Link
            to={routePath.talentDetails(val.id).path}
            className="font-medium text-primary ml-3"
          >
            {formatFullName(val.first_name, val.last_name, val.middle_name)}
          </Link>
        </div>
      ),
    },
    {
      title: "Present Job Role",
      render: (_, val) => (
        <span>{getCurrentJob(val?.work_histories)?.title ?? null}</span>
      ),
    },
    {
      title: "Present Employer",
      render: (_, val) => (
        <span>{getCurrentJob(val?.work_histories)?.company ?? null}</span>
      ),
    },
    {
      title: "Experience Level",
      render: (_, val) => (
        <span>{formatExperienceLevel(val?.experience_level ?? "")}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, val) => {
        const hasAnyAction =
          canCreateFolder || canCreateInvite || canViewMessages;

        if (!hasAnyAction) return null;

        return (
          <div>
            <Dropdown
              trigger={["click"]}
              disabled={selectedRowKeys.length > 0}
              overlay={
                <Menu>
                  <Menu.Item key="view">
                    <Link to={routePath.talentDetails(val.id).path}>
                      View profile
                    </Link>
                  </Menu.Item>
                  {canCreateFolder && (
                    <Menu.Item
                      key="folder"
                      onClick={() => handleAddToFolder(val.id)}
                    >
                      Add to folder
                    </Menu.Item>
                  )}
                  {canCreateInvite && (
                    <Menu.Item key="invite" onClick={() => sendInvite(val.id)}>
                      Send meeting invite
                    </Menu.Item>
                  )}
                  {canViewMessages && (
                    <Menu.Item
                      key="message"
                      onClick={() => handleSendMessage(val.id)}
                    >
                      Send message
                    </Menu.Item>
                  )}
                </Menu>
              }
            >
              <i className="ri-more-2-fill text-lg cursor-pointer"></i>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const IdToBePassed = (): string[] => {
    if (selectedRowKeys.length > 0) {
      return selectedRowKeys;
    }
    if (!talentId) {
      return [];
    }
    return [talentId];
  };

  return (
    <div data-tour="talent-table">
      <SendInvite
        id={talentId}
        open={inviteModal}
        handleClose={() => setInviteModal(false)}
      />
      <AddToFolderForm
        arrayId={IdToBePassed()}
        open={addToFolder}
        handleClose={() => handleClear()}
      />
      <ReportTalent
        id={IdToBePassed()}
        open={reportModaL}
        handleClose={() => setReportModaL(false)}
      />

      {hasSelected && (
        <div className="mt-7 flex items-center gap-3">
          <button
            className="button"
            style={{ background: "#D92D20" }}
            onClick={() => {
              setTalentId(undefined);
              setReportModaL(true);
            }}
          >
            Report
          </button>

          {canCreateFolder && (
            <AppButton
              label="Add to folder"
              variant="transparent"
              handleClick={() => setAddToFolder(true)}
            />
          )}
        </div>
      )}

      {isFetching ? (
        <TableRowShimmer />
      ) : (
        <Table
          className="bg-white rounded-md shadow border mt-8"
          columns={columns}
          rowClassName={() => "text-gray-500"}
          dataSource={
            filteredData?.map((item) => ({ ...item, key: item.id })) || []
          }
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys as string[]);
            },
          }}
          scroll={{ x: 550 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} Talents`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={onChange}
        />
      )}
      <SendMessageModal
        open={messageModalOpen}
        onCancel={() => setMessageModalOpen(false)}
        talentId={selectedTalentId}
      />
    </div>
  );
});
